// ============================================================================
// Module 16.0: Surveys & Questionnaires Service Client
// ============================================================================

import {
  SurveyDashboardKPIs,
  QuestionItem,
  Questionnaire,
  SurveyCampaign,
  ChecklistDefinition,
  ChecklistExecution,
  SurveyResponse,
  ResponseFlag,
} from './types';

const API_BASE = '/api/surveys';

export const surveysService = {
  // 1. Analytics & KPIs
  async getDashboardKPIs(): Promise<SurveyDashboardKPIs> {
    const res = await fetch(`${API_BASE}/analytics/dashboard`);
    const data = await res.json();
    return data.data;
  },

  async getKpis(): Promise<SurveyDashboardKPIs> {
    return this.getDashboardKPIs();
  },

  // 2. Question Library
  async getQuestions(): Promise<QuestionItem[]> {
    const res = await fetch(`${API_BASE}/questions`);
    const data = await res.json();
    return data.data;
  },

  async createQuestion(payload: {
    code: string;
    title: string;
    question_type: string;
    description?: string;
    is_mandatory?: boolean;
    options?: Array<{ label: string; score_value: number; is_critical_flag?: boolean }>;
  }): Promise<QuestionItem> {
    const res = await fetch(`${API_BASE}/questions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data.data;
  },

  // 3. Questionnaires
  async getQuestionnaires(): Promise<Questionnaire[]> {
    const res = await fetch(`${API_BASE}/questionnaires`);
    const data = await res.json();
    return data.data;
  },

  async createQuestionnaire(payload: {
    code: string;
    title: string;
    description?: string;
    category?: string;
    created_by: string;
    sections?: Array<{ title: string; description?: string; weight_percentage?: number }>;
  }): Promise<Questionnaire> {
    const res = await fetch(`${API_BASE}/questionnaires`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data.data;
  },

  async publishQuestionnaire(id: string): Promise<Questionnaire> {
    const res = await fetch(`${API_BASE}/questionnaires/${id}/publish`, {
      method: 'POST',
    });
    const data = await res.json();
    return data.data;
  },

  // 4. Surveys
  async getSurveys(): Promise<SurveyCampaign[]> {
    const res = await fetch(`${API_BASE}/surveys`);
    const data = await res.json();
    return data.data;
  },

  async createSurvey(payload: {
    code: string;
    title: string;
    description?: string;
    survey_type?: string;
    questionnaire_id: string;
    is_anonymous?: boolean;
    target_audience_type?: string;
    target_respondent_count?: number;
    created_by: string;
  }): Promise<SurveyCampaign> {
    const res = await fetch(`${API_BASE}/surveys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data.data;
  },

  async updateSurveyStatus(id: string, status: string): Promise<SurveyCampaign> {
    const res = await fetch(`${API_BASE}/surveys/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    const data = await res.json();
    return data.data;
  },

  // 5. Checklists
  async getChecklists(): Promise<ChecklistDefinition[]> {
    const res = await fetch(`${API_BASE}/checklists`);
    const data = await res.json();
    return data.data;
  },

  async createChecklist(payload: {
    code: string;
    title: string;
    category?: string;
    description?: string;
    created_by: string;
    items?: Array<{
      clause_reference?: string;
      title: string;
      description?: string;
      response_type?: string;
      is_critical_stop_work?: boolean;
      requires_evidence?: boolean;
    }>;
  }): Promise<ChecklistDefinition> {
    const res = await fetch(`${API_BASE}/checklists`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data.data;
  },

  async getChecklistExecutions(): Promise<ChecklistExecution[]> {
    const res = await fetch(`${API_BASE}/checklist-executions`);
    const data = await res.json();
    return data.data;
  },

  async executeChecklist(payload: {
    checklist_id: string;
    title: string;
    source_module: string;
    source_id: string;
    executor_user_id: string;
    answers: Array<{
      checklist_item_id: string;
      result: 'COMPLIANT' | 'NON_COMPLIANT' | 'PASS' | 'FAIL' | 'NOT_APPLICABLE';
      comments?: string;
    }>;
  }): Promise<ChecklistExecution> {
    const res = await fetch(`${API_BASE}/checklist-executions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data.data;
  },

  // 6. Responses
  async getResponses(surveyId?: string): Promise<SurveyResponse[]> {
    const url = surveyId ? `${API_BASE}/responses?surveyId=${surveyId}` : `${API_BASE}/responses`;
    const res = await fetch(url);
    const data = await res.json();
    return data.data;
  },

  async submitResponse(
    surveyId: string,
    payload: {
      respondent_user_id?: string;
      respondent_department?: string;
      completion_time_seconds?: number;
      answers: Array<{
        question_id: string;
        selected_option_id?: string;
        answer_text?: string;
        answer_numeric?: number;
      }>;
    }
  ): Promise<{ response: SurveyResponse; flagsCreated: ResponseFlag[] }> {
    const res = await fetch(`${API_BASE}/surveys/${surveyId}/responses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data.data;
  },

  // 7. Safety Flags
  async getFlags(): Promise<ResponseFlag[]> {
    const res = await fetch(`${API_BASE}/flags`);
    const data = await res.json();
    return data.data;
  },

  async getSafetyFlags(): Promise<ResponseFlag[]> {
    return this.getFlags();
  },

  async escalateFlag(
    id: string,
    payload: {
      target_type: 'HAZARD' | 'ACTION';
      target_id: string;
      reviewed_by: string;
    }
  ): Promise<ResponseFlag> {
    const res = await fetch(`${API_BASE}/flags/${id}/escalate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data.data;
  },
};
